/**
 * AI Context Manager
 * 
 * Manages AI conversation sessions and context to prevent sending full context
 * with every message. Implements smart context loading and session tracking.
 * 
 * @author AI Assistant
 * @version 1.0.0
 */

import { estimateTokens, estimatePromptTokens, validateTokenBudget, MAX_TOKENS_MEDIUM } from './tokenEstimator';

/**
 * Session states for AI conversations
 */
export const SESSION_STATES = {
  NEW: 'new',
  LOADING_CONTEXT: 'loading-context',
  CONTEXT_ESTABLISHED: 'context-established',
  CONTEXT_FAILED: 'context-failed',
  EXPIRED: 'expired'
};

/**
 * Context types for different payload structures
 */
export const CONTEXT_TYPES = {
  FULL: 'full',
  MINIMAL: 'minimal',
  REFERENCE: 'reference'
};

/**
 * Context addition states for multi-control management
 */
export const CONTEXT_ADDITION_STATES = {
  NONE_NEEDED: 'none-needed',           // Same control, no action
  READY_TO_ADD: 'ready-to-add',         // Control ready to add when chat starts
  ADDITION_PENDING: 'addition-pending', // New control detected
  ADDING_CONTEXT: 'adding-context',     // Currently adding context
  CONTEXT_ADDED: 'context-added',       // Successfully added
  ADDITION_FAILED: 'addition-failed',   // Failed to add
  LIMIT_REACHED: 'limit-reached'        // 5+ controls, need permission
};

/**
 * Control transition events
 */
export const TRANSITION_EVENTS = {
  CONTROL_CHANGED: 'control-changed',
  CONTEXT_ADDITION_NEEDED: 'context-addition-needed',
  CONTEXT_LIMIT_REACHED: 'context-limit-reached',
  CONTEXT_RESET_REQUESTED: 'context-reset-requested'
};

/**
 * AI Context Manager Class
 * Handles session-based context management for AI conversations
 */
export class AIContextManager {
  constructor() {
    this.sessions = new Map();
    this.contextCache = new Map();
    this.sessionTimeout = 30 * 60 * 1000; // 30 minutes
    this.maxSessions = 10; // Prevent memory leaks
    
    // Multi-control session management
    this.multiControlSessions = new Map(); // Track cross-control sessions
    this.contextHistory = new Map(); // Track context accumulation
    this.globalSessionCounter = 0; // For generating global session IDs
    this.defaultMaxContextLimit = 5; // Default limit for contexts per global session
    
    // Event listeners for control transitions
    this.transitionListeners = new Map();
    
    // Cleanup expired sessions periodically
    this.startCleanupTimer();
  }

  /**
   * Generate unique session ID
   * @returns {string} Unique session identifier
   */
  generateSessionId() {
    return `ai-session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Generate unique global session ID for multi-control sessions
   * @returns {string} Unique global session identifier
   */
  generateGlobalSessionId() {
    this.globalSessionCounter++;
    return `global-session-${Date.now()}-${this.globalSessionCounter}`;
  }

  /**
   * Create or get existing multi-control session
   * @param {string} controlId - Control identifier
   * @param {Object} controlContext - Control context data
   * @param {string} existingGlobalSessionId - Existing global session ID (optional)
   * @param {number} maxContextLimit - Maximum number of contexts allowed
   * @returns {Object} Multi-control session data
   */
  createOrGetMultiControlSession(controlId, controlContext, existingGlobalSessionId = null, maxContextLimit = null) {
    try {
      let globalSessionId = existingGlobalSessionId;
      let multiControlSession;

      if (globalSessionId && this.multiControlSessions.has(globalSessionId)) {
        // Get existing multi-control session
        multiControlSession = this.multiControlSessions.get(globalSessionId);
        console.log(`🔄 Using existing global session: ${globalSessionId}`);
      } else {
        // Create new multi-control session
        globalSessionId = this.generateGlobalSessionId();
        multiControlSession = {
          globalSessionId,
          controlSequence: [],
          contextStack: [],
          totalTokens: 0,
          maxContextLimit: maxContextLimit || this.defaultMaxContextLimit,
          currentControlId: null,
          contextAdditionState: CONTEXT_ADDITION_STATES.NONE_NEEDED,
          lastContextAddition: null,
          createdAt: Date.now(),
          lastActivity: Date.now()
        };
        
        this.multiControlSessions.set(globalSessionId, multiControlSession);
        console.log(`🆕 Created new global session: ${globalSessionId}`);
      }

      // Check if this is a new control
      const isNewControl = !multiControlSession.controlSequence.includes(controlId);
      
      if (isNewControl) {
        // Add new control to the sequence
        const contextTokens = estimateTokens(JSON.stringify(controlContext));
        
        multiControlSession.controlSequence.push(controlId);
        // Extract control name for display purposes - use controlTitle
        const extractedControlName = controlContext?.controlTitle || 
                                    controlContext?.controlId || 
                                    controlId;
        
        console.log('🏷️ Storing control in multi-control session:', {
          controlId,
          extractedControlName,
          contextStructure: {
            hasControlTitle: !!controlContext?.controlTitle,
            controlTitle: controlContext?.controlTitle,
            controlIdFromContext: controlContext?.controlId,
            contextKeys: Object.keys(controlContext || {})
          }
        });

        multiControlSession.contextStack.push({
          controlId,
          context: controlContext,
          tokens: contextTokens,
          addedAt: Date.now(),
          // Store control name for display purposes
          controlName: extractedControlName
        });
        
        multiControlSession.totalTokens += contextTokens;
        multiControlSession.currentControlId = controlId;
        multiControlSession.lastActivity = Date.now();
        
        // Determine context addition state
        if (!multiControlSession.controlSequence || multiControlSession.controlSequence.length === 1) {
          multiControlSession.contextAdditionState = CONTEXT_ADDITION_STATES.NONE_NEEDED;
        } else if (multiControlSession.controlSequence.length <= multiControlSession.maxContextLimit) {
          multiControlSession.contextAdditionState = CONTEXT_ADDITION_STATES.ADDITION_PENDING;
        } else {
          multiControlSession.contextAdditionState = CONTEXT_ADDITION_STATES.LIMIT_REACHED;
        }
        
        console.log(`➕ Added control ${controlId} to global session (${multiControlSession.controlSequence.length} controls total)`);
      } else {
        // Switching back to existing control
        multiControlSession.currentControlId = controlId;
        multiControlSession.lastActivity = Date.now();
        multiControlSession.contextAdditionState = CONTEXT_ADDITION_STATES.NONE_NEEDED;
        
        console.log(`🔄 Switched back to existing control: ${controlId}`);
      }

      return multiControlSession;
    } catch (error) {
      console.error('Error creating/getting multi-control session:', error);
      throw new Error(`Failed to create multi-control session: ${error.message}`);
    }
  }

  /**
   * Get multi-control session by global session ID
   * @param {string} globalSessionId - Global session identifier
   * @returns {Object|null} Multi-control session data or null if not found
   */
  getMultiControlSession(globalSessionId) {
    if (!globalSessionId) return null;
    
    const session = this.multiControlSessions.get(globalSessionId);
    if (!session) return null;
    
    // Check if session is expired
    if (this.isMultiControlSessionExpired(session)) {
      this.expireMultiControlSession(globalSessionId);
      return null;
    }
    
    return session;
  }

  /**
   * Check if multi-control session is expired
   * @private
   */
  isMultiControlSessionExpired(session) {
    return (Date.now() - session.lastActivity) > this.sessionTimeout;
  }

  /**
   * Expire a multi-control session
   * @param {string} globalSessionId - Global session identifier
   */
  expireMultiControlSession(globalSessionId) {
    const session = this.multiControlSessions.get(globalSessionId);
    if (session) {
      console.log(`⏰ Multi-control session expired: ${globalSessionId}`);
      this.multiControlSessions.delete(globalSessionId);
      
      // Also expire related individual sessions
      session.controlSequence.forEach(controlId => {
        const individualSessions = Array.from(this.sessions.entries())
          .filter(([sessionId, session]) => session.controlId === controlId);
        
        individualSessions.forEach(([sessionId]) => {
          this.expireSession(sessionId);
        });
      });
    }
  }

  /**
   * Update context addition state for multi-control session
   * @param {string} globalSessionId - Global session identifier
   * @param {string} newState - New context addition state
   * @param {Object} additionalData - Additional data to update
   */
  updateContextAdditionState(globalSessionId, newState, additionalData = {}) {
    const session = this.multiControlSessions.get(globalSessionId);
    if (!session) return;
    
    session.contextAdditionState = newState;
    session.lastActivity = Date.now();
    
    if (newState === CONTEXT_ADDITION_STATES.CONTEXT_ADDED) {
      session.lastContextAddition = Date.now();
    }
    
    // Update additional data
    Object.assign(session, additionalData);
    
    console.log(`📝 Multi-control session ${globalSessionId} context state updated: ${newState}`);
    
    // Emit transition event
    this.emitTransitionEvent(TRANSITION_EVENTS.CONTEXT_ADDITION_NEEDED, {
      globalSessionId,
      state: newState,
      session,
      ...additionalData
    });
  }

  /**
   * Clear context for multi-control session (reset to single control)
   * @param {string} globalSessionId - Global session identifier
   * @param {string} keepControlId - Control ID to keep (optional)
   */
  clearMultiControlContext(globalSessionId, keepControlId = null) {
    const session = this.multiControlSessions.get(globalSessionId);
    if (!session) return;
    
    if (keepControlId && (session.controlSequence || []).includes(keepControlId)) {
      // Keep only the specified control
      const keepContext = (session.contextStack || []).find(ctx => ctx.controlId === keepControlId);
      
      session.controlSequence = [keepControlId];
      session.contextStack = keepContext ? [keepContext] : [];
      session.totalTokens = keepContext ? keepContext.tokens : 0;
      session.currentControlId = keepControlId;
      session.contextAdditionState = CONTEXT_ADDITION_STATES.NONE_NEEDED;
      
      console.log(`🧹 Cleared multi-control context, kept only: ${keepControlId}`);
    } else {
      // Clear all context
      session.controlSequence = [];
      session.contextStack = [];
      session.totalTokens = 0;
      session.currentControlId = null;
      session.contextAdditionState = CONTEXT_ADDITION_STATES.NONE_NEEDED;
      
      console.log(`🧹 Cleared all multi-control context for session: ${globalSessionId}`);
    }
    
    session.lastActivity = Date.now();
    
    // Emit reset event
    this.emitTransitionEvent(TRANSITION_EVENTS.CONTEXT_RESET_REQUESTED, {
      globalSessionId,
      session,
      keepControlId
    });
  }

  /**
   * Initialize a new AI session with control context
   * @param {string} controlId - Control identifier
   * @param {Object} controlContext - Full control context data
   * @returns {string} Session ID
   */
  initializeSession(controlId, controlContext) {
    try {
      const sessionId = this.generateSessionId();
      
      // Estimate context size
      const contextTokens = estimateTokens(JSON.stringify(controlContext));
      
      const session = {
        sessionId,
        controlId,
        state: SESSION_STATES.NEW,
        contextSent: false,
        conversationHistory: [],
        startTime: Date.now(),
        lastActivity: Date.now(),
        contextTokens,
        totalTokensUsed: 0,
        messageCount: 0,
        contextEstablishedAt: null
      };
      
      // Store session
      this.sessions.set(sessionId, session);
      
      // Cache context for reuse
      this.contextCache.set(controlId, {
        context: controlContext,
        tokens: contextTokens,
        cachedAt: Date.now()
      });
      
      // Cleanup old sessions if needed
      this.cleanupOldSessions();
      
      console.log(`🆕 AI Session initialized: ${sessionId} (${contextTokens} context tokens)`);
      
      return sessionId;
    } catch (error) {
      console.error('Error initializing AI session:', error);
      throw new Error(`Failed to initialize AI session: ${error.message}`);
    }
  }

  /**
   * Get session information
   * @param {string} sessionId - Session identifier
   * @returns {Object|null} Session data or null if not found
   */
  getSession(sessionId) {
    if (!sessionId) return null;
    
    const session = this.sessions.get(sessionId);
    if (!session) return null;
    
    // Check if session is expired
    if (this.isSessionExpired(session)) {
      this.expireSession(sessionId);
      return null;
    }
    
    return session;
  }

  /**
   * Update session state
   * @param {string} sessionId - Session identifier
   * @param {string} newState - New session state
   * @param {Object} additionalData - Additional data to update
   */
  updateSessionState(sessionId, newState, additionalData = {}) {
    const session = this.sessions.get(sessionId);
    if (!session) return;
    
    session.state = newState;
    session.lastActivity = Date.now();
    
    // Update additional data
    Object.assign(session, additionalData);
    
    // Special handling for context established
    if (newState === SESSION_STATES.CONTEXT_ESTABLISHED && !session.contextEstablishedAt) {
      session.contextEstablishedAt = Date.now();
      session.contextSent = true;
    }
    
    console.log(`📝 Session ${sessionId} state updated: ${newState}`);
  }

  /**
   * Build optimized payload based on session state
   * @param {string} sessionId - Session identifier
   * @param {string} message - User message
   * @param {Object} options - Additional options
   * @returns {Object} Optimized payload
   */
  buildPayload(sessionId, message, options = {}) {
    try {
      const session = this.getSession(sessionId);
      if (!session) {
        throw new Error('Session not found or expired');
      }

      const { controlId } = session;
      const cachedContext = this.contextCache.get(controlId);
      
      let payload;
      
      if (!session.contextSent) {
        // First message - send full context
        payload = this.buildFullContextPayload(session, message, cachedContext, options);
      } else {
        // Follow-up message - minimal context
        payload = this.buildMinimalContextPayload(session, message, options);
      }
      
      // Update session tracking
      session.messageCount++;
      session.lastActivity = Date.now();
      
      // Estimate payload tokens
      const payloadTokens = estimateTokens(JSON.stringify(payload));
      session.totalTokensUsed += payloadTokens;
      
      console.log(`📤 Payload built for ${sessionId}: ${payloadTokens} tokens (${payload.contextType} context)`);
      
      return payload;
    } catch (error) {
      console.error('Error building payload:', error);
      throw new Error(`Failed to build payload: ${error.message}`);
    }
  }

  /**
   * Build full context payload for first message
   * @private
   */
  buildFullContextPayload(session, message, cachedContext, options) {
    // Check if this is part of a multi-control session
    const multiControlSession = this.getMultiControlSessionByControlId(session.controlId);
    
    console.log('🔍 Multi-control session check:', {
      controlId: session.controlId,
      multiControlSession: multiControlSession ? {
        globalSessionId: multiControlSession.globalSessionId,
        controlSequence: multiControlSession.controlSequence,
        sequenceLength: multiControlSession.controlSequence?.length
      } : null,
      hasMultipleControls: multiControlSession && multiControlSession.controlSequence && multiControlSession.controlSequence.length > 1
    });
    
    if (multiControlSession && multiControlSession.controlSequence && multiControlSession.controlSequence.length > 1) {
      console.log('✅ Using multi-control context payload');
      // Multi-control context payload
      return this.buildMultiControlContextPayload(session, message, multiControlSession, options);
    }
    
    // Single control context payload (existing format)
    const payload = {
      message,
      context: cachedContext?.context || {},
      sessionId: session.sessionId,
      // Add metadata for internal tracking (backend should ignore unknown fields)
      _contextType: CONTEXT_TYPES.FULL,
      _isFirstMessage: true,
      _sessionMetadata: {
        controlId: session.controlId,
        sessionStartTime: session.startTime,
        estimatedContextTokens: session.contextTokens
      },
      ...options
    };
    
    return payload;
  }

  /**
   * Build minimal context payload for follow-up messages
   * @private
   */
  buildMinimalContextPayload(session, message, options) {
    // Check if this is part of a multi-control session
    const multiControlSession = this.getMultiControlSessionByControlId(session.controlId);
    
    if (multiControlSession && multiControlSession.controlSequence && multiControlSession.controlSequence.length > 1) {
      // Multi-control minimal payload
      return this.buildMultiControlMinimalPayload(session, message, multiControlSession, options);
    }
    
    // Single control minimal payload (existing format)
    const payload = {
      message,
      context: {
        // Send minimal context reference instead of full context
        _sessionReference: session.sessionId,
        _useExistingContext: true
      },
      sessionId: session.sessionId,
      // Add metadata for internal tracking
      _contextType: CONTEXT_TYPES.MINIMAL,
      _isFollowUp: true,
      _sessionMetadata: {
        messageCount: session.messageCount,
        sessionDuration: Date.now() - session.startTime,
        totalTokensUsed: session.totalTokensUsed
      },
      ...options
    };
    
    return payload;
  }

  /**
   * Build multi-control context payload
   * @private
   */
  buildMultiControlContextPayload(session, message, multiControlSession, options) {
    const previousControls = (multiControlSession.contextStack || [])
      .filter(ctx => ctx.controlId !== session.controlId)
      .map(ctx => ({
        controlId: ctx.controlId,
        context: ctx.context,
        tokens: ctx.tokens,
        addedAt: ctx.addedAt
      }));
    
    console.log('🔄 Building multi-control payload:', {
      currentControlId: session.controlId,
      totalControls: multiControlSession.controlSequence?.length || 0,
      previousControlsCount: previousControls.length,
      contextStackLength: multiControlSession.contextStack?.length || 0,
      globalSessionId: multiControlSession.globalSessionId
    });
    
    const payload = {
      message,
      sessionId: session.sessionId,
      
      // Multi-control context structure
      context: {
        // Current control context (primary) - get from contextStack, not contextCache
        currentControl: {
          controlId: session.controlId,
          context: multiControlSession.contextStack.find(ctx => ctx.controlId === session.controlId)?.context || {}
        },
        
        // Previous controls context (for cross-reference)
        previousControls: previousControls,
        
        // Context management metadata
        contextMetadata: {
          totalControls: multiControlSession.controlSequence?.length || 0,
          controlSequence: multiControlSession.controlSequence || [],
          totalTokens: multiControlSession.totalTokens || 0,
          contextAdditionState: multiControlSession.contextAdditionState,
          isMultiControlSession: true
        }
      },
      
      // Enhanced metadata for backend processing
      _contextType: CONTEXT_TYPES.FULL,
      _isMultiControl: true,
      _contextAction: 'establish-multi-control',
      _globalSessionId: multiControlSession.globalSessionId,
      _sessionMetadata: {
        controlId: session.controlId,
        globalSessionId: multiControlSession.globalSessionId,
        sessionStartTime: session.startTime,
        estimatedContextTokens: session.contextTokens,
        totalContextTokens: multiControlSession.totalTokens || 0,
        controlCount: multiControlSession.controlSequence?.length || 0,
        contextAdditionState: multiControlSession.contextAdditionState
      },
      
      ...options
    };
    
    return payload;
  }

  /**
   * Build multi-control minimal payload for follow-up messages
   * @private
   */
  buildMultiControlMinimalPayload(session, message, multiControlSession, options) {
    const payload = {
      message,
      sessionId: session.sessionId,
      
      // Minimal multi-control context
      context: {
        // Reference to established multi-control context
        _globalSessionReference: multiControlSession.globalSessionId,
        _useMultiControlContext: true,
        _currentControlId: session.controlId,
        
        // Essential context metadata
        contextMetadata: {
          totalControls: multiControlSession.controlSequence?.length || 0,
          controlSequence: multiControlSession.controlSequence || [],
          isMultiControlSession: true
        }
      },
      
      // Enhanced metadata
      _contextType: CONTEXT_TYPES.MINIMAL,
      _isMultiControl: true,
      _contextAction: 'continue-multi-control',
      _globalSessionId: multiControlSession.globalSessionId,
      _sessionMetadata: {
        controlId: session.controlId,
        globalSessionId: multiControlSession.globalSessionId,
        messageCount: session.messageCount,
        sessionDuration: Date.now() - session.startTime,
        totalTokensUsed: session.totalTokensUsed,
        controlCount: multiControlSession.controlSequence?.length || 0
      },
      
      ...options
    };
    
    return payload;
  }

  /**
   * Get multi-control session by control ID
   * @private
   */
  getMultiControlSessionByControlId(controlId) {
    for (const [globalSessionId, multiSession] of this.multiControlSessions.entries()) {
      if (multiSession.controlSequence.includes(controlId)) {
        return multiSession;
      }
    }
    return null;
  }

  /**
   * Check if a control is already in any multi-control session
   * @param {string} controlId - Control identifier to check
   * @returns {string|null} Global session ID if found, null otherwise
   */
  isControlInAnySession(controlId) {
    for (const [globalSessionId, multiSession] of this.multiControlSessions.entries()) {
      if (multiSession.controlSequence.includes(controlId)) {
        return globalSessionId;
      }
    }
    return null;
  }

  /**
   * Add control to context on-demand (when user starts chatting)
   * @param {string} controlId - Control identifier
   * @param {Object} controlContext - Control context data
   * @param {string} existingGlobalSessionId - Existing global session ID (optional)
   * @param {number} maxContextLimit - Maximum number of contexts allowed
   * @returns {Object} Multi-control session data
   */
  addControlOnChatStart(controlId, controlContext, existingGlobalSessionId = null, maxContextLimit = null) {
    console.log(`💬 Adding control to context on chat start: ${controlId}`);
    
    // Use existing createOrGetMultiControlSession method
    return this.createOrGetMultiControlSession(
      controlId,
      controlContext,
      existingGlobalSessionId,
      maxContextLimit
    );
  }

  /**
   * Add message to conversation history
   * @param {string} sessionId - Session identifier
   * @param {string} userMessage - User message
   * @param {string} aiResponse - AI response
   * @param {Object} metadata - Additional metadata
   */
  addToHistory(sessionId, userMessage, aiResponse, metadata = {}) {
    const session = this.getSession(sessionId);
    if (!session) return;
    
    const historyEntry = {
      timestamp: Date.now(),
      userMessage,
      aiResponse,
      tokens: estimateTokens(userMessage + aiResponse),
      ...metadata
    };
    
    session.conversationHistory.push(historyEntry);
    
    // Keep only last 10 exchanges to prevent memory bloat
    if (session.conversationHistory.length > 10) {
      session.conversationHistory = session.conversationHistory.slice(-10);
    }
  }

  /**
   * Get session analytics
   * @param {string} sessionId - Session identifier
   * @returns {Object} Session analytics data
   */
  getSessionAnalytics(sessionId) {
    const session = this.getSession(sessionId);
    if (!session) return null;
    
    const duration = Date.now() - session.startTime;
    const avgTokensPerMessage = session.messageCount > 0 ? session.totalTokensUsed / session.messageCount : 0;
    
    return {
      sessionId,
      controlId: session.controlId,
      state: session.state,
      duration,
      messageCount: session.messageCount,
      totalTokensUsed: session.totalTokensUsed,
      avgTokensPerMessage: Math.round(avgTokensPerMessage),
      contextTokens: session.contextTokens,
      tokenSavings: this.calculateTokenSavings(session),
      conversationLength: session.conversationHistory.length,
      contextEstablishedAt: session.contextEstablishedAt,
      isActive: !this.isSessionExpired(session)
    };
  }

  /**
   * Calculate token savings from context optimization
   * @private
   */
  calculateTokenSavings(session) {
    if (session.messageCount <= 1) return 0;
    
    // Calculate what tokens would have been without optimization
    const tokensWithoutOptimization = session.messageCount * session.contextTokens;
    const actualTokensUsed = session.totalTokensUsed;
    
    return Math.max(0, tokensWithoutOptimization - actualTokensUsed);
  }

  /**
   * Check if session is expired
   * @private
   */
  isSessionExpired(session) {
    return (Date.now() - session.lastActivity) > this.sessionTimeout;
  }

  /**
   * Expire a session
   * @param {string} sessionId - Session identifier
   */
  expireSession(sessionId) {
    const session = this.sessions.get(sessionId);
    if (session) {
      session.state = SESSION_STATES.EXPIRED;
      console.log(`⏰ Session expired: ${sessionId}`);
    }
  }

  /**
   * Clean up old sessions
   * @private
   */
  cleanupOldSessions() {
    const now = Date.now();
    const sessionsToDelete = [];
    
    for (const [sessionId, session] of this.sessions.entries()) {
      if (this.isSessionExpired(session)) {
        sessionsToDelete.push(sessionId);
      }
    }
    
    // Remove expired sessions
    sessionsToDelete.forEach(sessionId => {
      this.sessions.delete(sessionId);
      console.log(`🗑️ Cleaned up expired session: ${sessionId}`);
    });
    
    // If we have too many sessions, remove oldest ones
    if (this.sessions.size > this.maxSessions) {
      const sortedSessions = Array.from(this.sessions.entries())
        .sort(([,a], [,b]) => a.lastActivity - b.lastActivity);
      
      const toRemove = sortedSessions.slice(0, this.sessions.size - this.maxSessions);
      toRemove.forEach(([sessionId]) => {
        this.sessions.delete(sessionId);
        console.log(`🗑️ Cleaned up old session: ${sessionId}`);
      });
    }
  }

  /**
   * Event system for control transitions
   */

  /**
   * Add event listener for transition events
   * @param {string} eventType - Event type from TRANSITION_EVENTS
   * @param {Function} listener - Event listener function
   * @returns {string} Listener ID for removal
   */
  addTransitionListener(eventType, listener) {
    if (!this.transitionListeners.has(eventType)) {
      this.transitionListeners.set(eventType, new Map());
    }
    
    const listenerId = `listener-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.transitionListeners.get(eventType).set(listenerId, listener);
    
    console.log(`📡 Added transition listener for ${eventType}: ${listenerId}`);
    return listenerId;
  }

  /**
   * Remove event listener
   * @param {string} eventType - Event type
   * @param {string} listenerId - Listener ID
   */
  removeTransitionListener(eventType, listenerId) {
    if (this.transitionListeners.has(eventType)) {
      this.transitionListeners.get(eventType).delete(listenerId);
      console.log(`📡 Removed transition listener: ${listenerId}`);
    }
  }

  /**
   * Emit transition event
   * @param {string} eventType - Event type
   * @param {Object} eventData - Event data
   */
  emitTransitionEvent(eventType, eventData) {
    if (this.transitionListeners.has(eventType)) {
      const listeners = this.transitionListeners.get(eventType);
      listeners.forEach((listener, listenerId) => {
        try {
          listener(eventData);
        } catch (error) {
          console.error(`Error in transition listener ${listenerId}:`, error);
        }
      });
      
      console.log(`📡 Emitted transition event ${eventType} to ${listeners.size} listeners`);
    }
  }

  /**
   * Start cleanup timer
   * @private
   */
  startCleanupTimer() {
    setInterval(() => {
      this.cleanupOldSessions();
      this.cleanupOldMultiControlSessions();
    }, 5 * 60 * 1000); // Cleanup every 5 minutes
  }

  /**
   * Clean up old multi-control sessions
   * @private
   */
  cleanupOldMultiControlSessions() {
    const now = Date.now();
    const sessionsToDelete = [];
    
    for (const [globalSessionId, session] of this.multiControlSessions.entries()) {
      if (this.isMultiControlSessionExpired(session)) {
        sessionsToDelete.push(globalSessionId);
      }
    }
    
    // Remove expired sessions
    sessionsToDelete.forEach(globalSessionId => {
      this.expireMultiControlSession(globalSessionId);
    });
    
    if (sessionsToDelete.length > 0) {
      console.log(`🗑️ Cleaned up ${sessionsToDelete.length} expired multi-control sessions`);
    }
  }

  /**
   * Get all active sessions (for debugging)
   * @returns {Array} Array of session analytics
   */
  getAllSessions() {
    return Array.from(this.sessions.keys()).map(sessionId => 
      this.getSessionAnalytics(sessionId)
    ).filter(Boolean);
  }

  /**
   * Reset all sessions (for debugging/testing)
   */
  resetAllSessions() {
    this.sessions.clear();
    this.contextCache.clear();
    console.log('🔄 All AI sessions reset');
  }
}

// Create singleton instance
export const aiContextManager = new AIContextManager();

// Export utility functions
export const createAISession = (controlId, controlContext) => {
  return aiContextManager.initializeSession(controlId, controlContext);
};

export const getAISession = (sessionId) => {
  return aiContextManager.getSession(sessionId);
};

export const buildAIPayload = (sessionId, message, options) => {
  return aiContextManager.buildPayload(sessionId, message, options);
};

export const updateAISessionState = (sessionId, newState, additionalData) => {
  return aiContextManager.updateSessionState(sessionId, newState, additionalData);
};

export const getAISessionAnalytics = (sessionId) => {
  return aiContextManager.getSessionAnalytics(sessionId);
};

// Multi-control session management exports
export const createMultiControlSession = (controlId, controlContext, existingGlobalSessionId, maxContextLimit) => {
  return aiContextManager.createOrGetMultiControlSession(controlId, controlContext, existingGlobalSessionId, maxContextLimit);
};

export const getMultiControlSession = (globalSessionId) => {
  return aiContextManager.getMultiControlSession(globalSessionId);
};

export const updateMultiControlContextState = (globalSessionId, newState, additionalData) => {
  return aiContextManager.updateContextAdditionState(globalSessionId, newState, additionalData);
};

// New exports for chat-triggered context addition
export const isControlInAnySession = (controlId) => {
  return aiContextManager.isControlInAnySession(controlId);
};

export const addControlOnChatStart = (controlId, controlContext, existingGlobalSessionId, maxContextLimit) => {
  return aiContextManager.addControlOnChatStart(controlId, controlContext, existingGlobalSessionId, maxContextLimit);
};

export const clearMultiControlContext = (globalSessionId, keepControlId) => {
  return aiContextManager.clearMultiControlContext(globalSessionId, keepControlId);
};

export const addTransitionListener = (eventType, listener) => {
  return aiContextManager.addTransitionListener(eventType, listener);
};

export const removeTransitionListener = (eventType, listenerId) => {
  return aiContextManager.removeTransitionListener(eventType, listenerId);
};

export default aiContextManager;
